import { useEffect, useMemo, useState } from 'react';
import { Link2, RefreshCw, SearchX } from 'lucide-react';
import {
  CANDIDATE_TABS,
  LINK_CONFIDENCES,
  PRODUCTION_CLASSIFICATIONS,
  type CandidateGroup,
  type CandidateTab,
  type LinkConfidence,
  type ProductionClassificationValue,
} from '@quds/shared';
import { useCan } from '@/auth/AuthContext';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  FilterSelect,
  SearchInput,
  SkeletonRows,
  Table,
  TableContainer,
  TBody,
  TD,
  TH,
  THead,
  TR,
  useToast,
} from '@/components/ui';
import {
  bootstrapProductionClassifications,
  getProductionStatus,
  listProductionCandidates,
  useQuery,
} from '@/data';
import { errorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';
import { CandidateReviewPanel } from './CandidateReviewPanel';
import { ProductionConnectionBadge } from './ProductionBadges';
import {
  CATEGORY_LABELS,
  CLASSIFICATION_LABELS,
  CONFIDENCE_LABELS,
  CONFIDENCE_TONES,
  TAB_LABELS,
} from './labels';

const COLUMN_COUNT = 5;

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function highestConfidence(group: CandidateGroup): LinkConfidence | null {
  const order: LinkConfidence[] = ['conflict', 'high', 'medium', 'low', 'manual'];
  const all = [...group.items, ...group.conflicts];
  for (const c of order) {
    if (all.some((i) => i.confidence === c)) return c;
  }
  return null;
}

export function ProductionLinkingPage() {
  const can = useCan();
  const canLink = can('production.link');
  const { notify } = useToast();

  const [tab, setTab] = useState<CandidateTab>('candidates');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounced(searchInput, 250);
  const [confidence, setConfidence] = useState('');
  const [classification, setClassification] = useState('');
  const [selected, setSelected] = useState<CandidateGroup | null>(null);
  const [bootstrapping, setBootstrapping] = useState(false);

  const query = useMemo(
    () => ({
      tab,
      q: debouncedSearch || undefined,
      confidence: (confidence || undefined) as LinkConfidence | undefined,
      classification: (classification || undefined) as ProductionClassificationValue | undefined,
    }),
    [tab, debouncedSearch, confidence, classification],
  );

  const { data: status, reload: reloadStatus } = useQuery(getProductionStatus, []);
  const { data, error, loading, refreshing, reload } = useQuery(() => listProductionCandidates(query), [query]);

  const refreshAll = () => {
    reloadStatus();
    reload();
  };

  const filtersActive = !!(debouncedSearch.trim() || confidence || classification);
  const clearFilters = () => {
    setSearchInput('');
    setConfidence('');
    setClassification('');
  };

  const onBootstrap = async () => {
    setBootstrapping(true);
    try {
      const result = await bootstrapProductionClassifications();
      notify({
        tone: 'success',
        title: 'Phase 3A classifications applied',
        description: result.applied === 0 ? 'All suggestions were already classified.' : `${result.applied} classification${result.applied === 1 ? '' : 's'} written.`,
      });
      refreshAll();
    } catch (err) {
      notify({ tone: 'error', title: 'Bootstrap failed', description: errorMessage(err) });
    } finally {
      setBootstrapping(false);
    }
  };

  const connectionState = status?.state ?? (data?.productionStatus as { state?: string } | undefined)?.state;
  const unavailable = status?.unavailable ?? data?.unavailable;

  return (
    <>
      <PageHeader
        title="Production Linking"
        subtitle="Review production identities and confirm links to Central Admin businesses"
        actions={
          <>
            <ProductionConnectionBadge state={connectionState} unavailable={unavailable} />
            {canLink && (
              <Button variant="secondary" size="sm" loading={bootstrapping} onClick={() => void onBootstrap()}>
                Apply Phase 3A suggestions
              </Button>
            )}
            <Button
              variant="secondary"
              leftIcon={<RefreshCw size={16} aria-hidden="true" />}
              onClick={refreshAll}
              disabled={loading || refreshing}
            >
              Refresh
            </Button>
          </>
        }
      />

      {(unavailable || connectionState === 'unavailable' || connectionState === 'disabled' || connectionState === 'integration_disabled') && (
        <div
          role="status"
          className="mb-4 rounded-md border border-line-strong bg-surface-sunken px-4 py-3 text-sm text-ink-secondary"
        >
          {connectionState === 'disabled' || connectionState === 'integration_disabled'
            ? 'Production integration is turned off. Central Admin master data is unaffected.'
            : 'Production is temporarily unavailable — Central Admin data is unaffected. Candidate lists may be empty until the connection recovers.'}
        </div>
      )}

      <div className="card">
        <div className="border-b border-line px-2 pt-2 sm:px-3">
          <div role="tablist" aria-label="Candidate tabs" className="flex gap-1 overflow-x-auto">
            {CANDIDATE_TABS.map((t) => {
              const active = tab === t;
              return (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTab(t)}
                  className={cn(
                    'shrink-0 rounded-md px-3 py-2 text-sm transition-colors focus-visible:shadow-focus focus-visible:outline-none',
                    active ? 'bg-surface-sunken font-semibold text-ink' : 'text-ink-secondary hover:bg-surface-sunken/70 hover:text-ink',
                  )}
                >
                  {TAB_LABELS[t]}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col gap-3 border-b border-line p-4 lg:flex-row lg:items-center">
          <SearchInput
            label="Search candidates"
            placeholder="Search name, id or group…"
            value={searchInput}
            onChange={setSearchInput}
            className="w-full lg:max-w-xs"
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex lg:items-center">
            <FilterSelect
              label="Confidence"
              allLabel="All"
              value={confidence}
              onChange={setConfidence}
              options={LINK_CONFIDENCES.map((c) => ({ value: c, label: CONFIDENCE_LABELS[c] }))}
              className="lg:w-48"
            />
            <FilterSelect
              label="Classification"
              allLabel="All"
              value={classification}
              onChange={setClassification}
              options={PRODUCTION_CLASSIFICATIONS.map((c) => ({ value: c, label: CLASSIFICATION_LABELS[c] }))}
              className="lg:w-48"
            />
          </div>
          {filtersActive && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="self-start lg:self-auto">
              Clear filters
            </Button>
          )}
        </div>

        {error ? (
          <ErrorState message={error.message} action={<Button variant="secondary" onClick={reload}>Try again</Button>} />
        ) : (
          <TableContainer label="Production candidates">
            <Table className="min-w-[880px]" aria-busy={loading || refreshing}>
              <THead>
                <tr>
                  <TH className="pl-5">Proposed name</TH>
                  <TH>Category</TH>
                  <TH>Identities</TH>
                  <TH>Confidence</TH>
                  <TH align="right" className="pr-5">
                    Actions
                  </TH>
                </tr>
              </THead>
              <TBody className={cn('transition-opacity', refreshing && 'opacity-60')}>
                {loading ? (
                  <SkeletonRows rows={8} columns={COLUMN_COUNT} />
                ) : data && data.items.length > 0 ? (
                  data.items.map((g) => {
                    const top = highestConfidence(g);
                    return (
                      <TR key={g.groupKey} interactive>
                        <TD className="max-w-[280px] pl-5">
                          <button
                            type="button"
                            className="block truncate text-left font-medium text-ink hover:text-primary hover:underline focus-visible:shadow-focus focus-visible:outline-none"
                            onClick={() => setSelected(g)}
                          >
                            {g.proposedName}
                          </button>
                          <span className="block truncate font-mono text-xs text-ink-muted">{g.groupKey}</span>
                        </TD>
                        <TD>{CATEGORY_LABELS[g.proposedCategory]}</TD>
                        <TD>
                          <span className="tabular-nums text-ink">{g.items.length}</span>
                          {g.conflicts.length > 0 && (
                            <span className="ml-2 text-xs text-danger">{g.conflicts.length} conflict{g.conflicts.length === 1 ? '' : 's'}</span>
                          )}
                        </TD>
                        <TD>
                          {top ? (
                            <Badge tone={CONFIDENCE_TONES[top]}>{CONFIDENCE_LABELS[top]}</Badge>
                          ) : (
                            <span className="text-ink-muted">—</span>
                          )}
                        </TD>
                        <TD align="right" className="pr-5">
                          <Button variant="ghost" size="sm" onClick={() => setSelected(g)}>
                            Review
                          </Button>
                        </TD>
                      </TR>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={COLUMN_COUNT}>
                      <EmptyState
                        icon={filtersActive ? <SearchX size={20} aria-hidden="true" /> : <Link2 size={20} aria-hidden="true" />}
                        title={filtersActive ? 'No candidates match your filters' : `No ${TAB_LABELS[tab].toLowerCase()} yet`}
                        description={
                          filtersActive
                            ? 'Try a different search term or clear the filters.'
                            : unavailable
                              ? 'Candidates appear when production is connected and identities can be matched.'
                              : 'Nothing in this tab right now.'
                        }
                        action={
                          filtersActive ? (
                            <Button variant="secondary" onClick={clearFilters}>
                              Clear filters
                            </Button>
                          ) : undefined
                        }
                      />
                    </td>
                  </tr>
                )}
              </TBody>
            </Table>
          </TableContainer>
        )}
      </div>

      {data && data.total > 0 && (
        <p className="mt-3 text-xs text-ink-muted">
          Showing {data.items.length} group{data.items.length === 1 ? '' : 's'}
          {!canLink && ' · Read-only (production.link required for actions)'}
        </p>
      )}

      {selected && (
        <CandidateReviewPanel
          group={selected}
          onClose={() => setSelected(null)}
          onChanged={refreshAll}
        />
      )}
    </>
  );
}
